import { app, dialog, session } from "electron";
import { getOrCreateMainLogger } from "./logger/main";
import { getManifest } from "./main/Manifest";
import { setAppDataFolderName, appDataFolderPath, appDataLogsPath } from "./main/appDataFolder";
import { handleSquirrelEvents, updateManager } from "./main/updateManager";
import { isDevLaunch } from "./util";
import fs from "fs";
import path from "path";
import parseArgs from "minimist";

/**
 * Uncomment in order to see all logger messages in the VSC console.
 * The log level can be set by modifying logger/config.js
 */
// logger.enableConsole();

import { main } from "./main/Main";
import { getConfig } from "./main/Config";
const logger = getOrCreateMainLogger();

const args = parseArgs(process.argv);

let appName: string;
// Before the Electron app is ready, derive the app's name and then set the directory path for storing the app's configuration files (cache, indexedDB, etc)
const installedName = getConfig({ suppressLog: true })?.folderName;
const argsName = args["name"];
appName = installedName ?? argsName ?? app.name;

// For backward compatibility we don't use the command line parameters to derive the app folder name. This means
// the folder will be "Electron" when running in dev mode (through e2oLauncher).
const appFolderName = installedName ?? app.name;
const userDataPath = path.join(app.getPath("appData"), appFolderName, "userdata");
app.setPath("userData", userDataPath);
setAppDataFolderName(appFolderName);
// logger is not available yet (see logger note below), so use console
console.log(`Application folder: ${appDataFolderPath()}`);
const { loggingMaxSize = "5m", loggingMaxFiles = 3, sanitizingRegExes = [] } = getConfig({ suppressLog: true }) || {};
logger.initialize(appDataLogsPath(), loggingMaxFiles, loggingMaxSize, sanitizingRegExes);
logger.debug("FEA Logger config:", appDataLogsPath(), loggingMaxFiles, loggingMaxSize, sanitizingRegExes);

if (args["enable-console"]) logger.enableConsole();
logger.log("Command line args", JSON.stringify(args, null, 4));

try {
	const chromiumFlags = isDevLaunch()
		? JSON.parse(process.env.chromiumFlags ?? "{}")
		: (getConfig({ suppressLog: true })?.chromiumFlags ?? {});

	/**
	 * Adding this switch due to a Electron change 11, include in Chromium M87, where windows that are occluded and
	 * visibility would not re-render. It "Prevents Chromium from lowering the priority of
	 * invisible pages' renderer processes".
	 *
	 * @see https://blog.chromium.org/2020/11/tab-throttling-and-more-performance.html
	 * @see https://www.electronjs.org/docs/api/command-line-switches#--disable-renderer-backgrounding
	 */
	chromiumFlags["disable-renderer-backgrounding"] = "true";

	/**
	 * Disable backgrounding renders for occluded windows. Done for tests to avoid nondeterministic behavior.
	 */
	chromiumFlags["disable-backgrounding-occluded-windows"] = "true";

	const keys = chromiumFlags ? Object.keys(chromiumFlags) : [];
	keys.forEach((key) => {
		logger.info(`Appending chromium flags switch. Key: ${key}, Value: ${chromiumFlags[key]}`);
		app.commandLine.appendSwitch(key, chromiumFlags[key]);
	});
} catch (error) {
	logger.warn(`Unable to set chromium flags: ${error}. Continuing startup.`);
}

// Enable chromium process sandboxing globally for security.
// See https://www.electronjs.org/docs/latest/tutorial/sandbox
app.enableSandbox();

// https://docs.microsoft.com/en-us/windows/desktop/shell/appids
// Causes the multiple electron apps/windows to be grouped together under a single windows taskbar icon.
app.setAppUserModelId("e2o");

process.on("uncaughtException", (error) => {
	logger.error(`app.js->Uncaught exception`, error);
});

process.on("unhandledRejection", (reason, p) => {
	logger.error(`app.js->Unhandled rejection ${reason} promise ${p}`);
});

const checkForUpdates = (FEAConfig: any) => {
	try {
		const feedUrl = getConfig({ suppressLog: true })?.updateFeedUrl;
		if (feedUrl) {
			// Default for downloadUpdatesViaElectron is true because it's more reliable to use Electron to do the downloading since it obeys proxies
			updateManager.usePreloadUpdater(FEAConfig?.downloadUpdatesViaElectron ?? true);
			updateManager.setFeedUrl(feedUrl);
			updateManager.listenForNewUpdates();
		} else {
			logger.warn(
				`No update feed URL found. This application will not update. Please make sure the config in project.json has a value for 'updateUrl'.`
			);
		}
	} catch (e) {
		logger.warn(
			"Unable to find updateFeedUrl in deployed e2o. Ensure that updateUrl was supplied when creating the installer."
		);
	}
};

/**
 * Start the application. This will launch a smart desktop server if it has been enabled, fetch the manifest, and then start the main application.
 */
const start = async () => {
	let smartDesktopManifestURL: string | null = null;
	let appendImports: string[] = [];

	let options: any = {
		timeout: 30000,
		retries: 5,
	};
	if (args["manifest-timeout"]) {
		options.timeout = parseInt(args["manifest-timeout"]);
	}
	if (args["manifest-retries"]) {
		options.retries = parseInt(args["manifest-retries"]);
	}

	let manifest: any = await getManifest({ manifestURL: smartDesktopManifestURL, options, appendImports, appName });

	if (!isDevLaunch()) checkForUpdates((manifest as any)["finsemble-electron-adapter"]);
	return await main({ app, manifest });
};

// The installer will do some things here. If it needs to, it will return true and quit the app.(updates...)
// Later, we may need to implement updates.
const updateFound = handleSquirrelEvents({ app, userDataPath });

/**
 * Workaround to NODE_EXTRA_CA_CERTS environment variable not being respected in Electron.
 * See electron issue: https://github.com/electron/electron/issues/10257 for information on this issue and the workaround implemented.
 */
function useNodeExtraCaCerts() {
	if (process.env.NODE_EXTRA_CA_CERTS) {
		logger.log("NODE_EXTRA_CA_CERTS environment variable is set, reading in additional trusted CA certificates");
		let extraRootCerts: any;
		try {
			extraRootCerts = fs.readFileSync(process.env.NODE_EXTRA_CA_CERTS);
		} catch (e) {
			logger.warn("Error reading in CA certificate specified in NODE_EXTRA_CA_CERTS environment variable", e);
			return;
		}
		// @ts-ignore process.binding is an undocumented method for obtaining access to a C++ module
		const nativeSecureContext = process.binding("crypto").SecureContext;
		const oldPrototypeMethod = nativeSecureContext.prototype.addRootCerts;
		// Patches addRootCerts function to add certs read in in NODE_EXTRA_CA_CERTS.
		//Using an arrow function here throws an illegal invocation error
		nativeSecureContext.prototype.addRootCerts = function () {
			const newPrototypeMethod = oldPrototypeMethod.apply(this, arguments);
			this.addCACert(extraRootCerts);
			return newPrototypeMethod;
		};
	}
}

/**
 * Shows a blocking OS level dialog with an error message and presents the user the option to either
 * retry or quit Finsemble startup.
 *
 * The dialog output will be the index of the clicked button in the buttons array, which will be casted to a boolean
 * representing whether the user wants to retry or otherwise.
 *
 * @param error - The error message that will be displayed in the dialog
 * @returns A boolean whether the user wants to quit or retry the Finsemble startup.
 */
const doesUserWantARetry = (error: any) =>
	!!dialog.showMessageBoxSync({
		buttons: ["Quit", "Retry"],
		type: "error",
		message: error,
	});

if (!updateFound) {
	// When electron is ready: check for updates, get the manifest, start e2o
	app.on("ready", async () => {
		logger.log("APPLICATION LIFECYCLE: Electron app ready.");
		// https://github.com/electron/electron/issues/26039
		// https://github.com/electron/electron/issues/22995
		session.defaultSession.setSpellCheckerDictionaryDownloadURL("https://00.00/");
		session.defaultSession.allowNTLMCredentialsForDomains("*");
		if (isDevLaunch()) useNodeExtraCaCerts();
		let wantsARetry: boolean = false;
		let error: string | null = null;
		// When Finsemble fails to start (e.g. no network connection, failure to retrieve manifest)
		// we display an OS level dialog in which we present the error and give the user control
		// on whether we want to retry to start Finsemble or quit which will abandon the startup
		// process and exit Finsemble
		do {
			try {
				error = await start();
				// Stop retry attempts; this is necessary in the event that
				// Finsemble encounters a problem loading the manifest, the user
				// retries and a subsequent load attempt is works.
				wantsARetry = false;
			} catch (err: any) {
				wantsARetry = doesUserWantARetry(`Error: ${err}`);
				if (wantsARetry) {
					logger.log(`Retrying Finsemble startup...`);
					console.log(`Retrying Finsemble startup...`);
				}
				error = err;
			}
		} while (wantsARetry);

		if (error) {
			logger.error(error);
			console.error(error);
			if (!isDevLaunch()) dialog.showErrorBox("Startup Error", error);
			app.quit(); // Don't use app.exit() because then the above console.error() won't have time to output
		}
	});
}
